<script>
  import { onMount, onDestroy } from 'svelte';
  import { persist } from '../../store.js';
  import { currentInnings, recordRuns, recordWide, recordNoBall,
    recordBye, recordLegBye, undoLastBall,
    formatOvers, isInningsOver, isAwaitingNewBowler
  } from '../../scoring-logic.js';
  import { activeMatch, currentScreen, fullScreenBoard, modalState, autoSplitInProgress, inningsEndPending, lastBallState } from '../store.js';
  import {
    doRecord, openExtraPicker, openWicketPicker,
    withBallGuard, afterBall,
    isLastManStanding, cancelPendingInningsEnd, proceedAfterInningsEnd
  } from '../../match-actions.js';
  import { triggerAnim } from '../../broadcast/broadcast-service.js';
  import { buzz } from '../../haptics.js';
  import { toast } from '../../ui/dialogs.js';

  export let match;

  $: inn = match ? currentInnings(match) : null;
  $: lastMan = match && inn ? isLastManStanding(match, inn) : false;
  $: ready = inn && inn.currentStriker && (inn.currentNonStriker || lastMan) && inn.currentBowler && !$autoSplitInProgress && !$inningsEndPending && board !== 'winner' && board !== 'match' && !isInningsOver(match) && !isAwaitingNewBowler(match);
  $: undoAvail = match && match._undoStack && match._undoStack.length > 0;
  $: freeHit = inn ? inn.freeHit : false;
  // The pad silently greys out for ~6.5s after every over while the over-summary
  // animation plays — surface the reason and let the scorer skip the wait.
  $: awaitingBowler = match && !$modalState && !board && !$inningsEndPending &&
    !isInningsOver(match) && isAwaitingNewBowler(match);
  $: awaitingBatter = inn && (!inn.currentStriker || !inn.currentNonStriker) && !lastMan;

  // In single-phone record mode this pad is the only scoring surface, but the
  // last-ball flash strip lives on the Scoring screen — mirror it here.
  let lastBallTimer = null;
  onMount(() => {
    const unsubFlash = lastBallState.subscribe(value => {
      if (lastBallTimer) clearTimeout(lastBallTimer);
      if (value && $currentScreen === 'recording') {
        lastBallTimer = setTimeout(() => lastBallState.set(null), 3000);
      }
    });
    return () => unsubFlash();
  });
  onDestroy(() => { if (lastBallTimer) clearTimeout(lastBallTimer); });

  function describeBall(b) {
    if (!b) return 'LAST BALL';
    if (b.type === 'wicket') return 'WICKET';
    if (b.type === 'wide') return b.runs + ' WIDE';
    if (b.type === 'nb') return 'NO BALL + ' + Math.max(0, b.runs - 1);
    if (b.type === 'b') return b.runs + ' BYE';
    if (b.type === 'lb') return b.runs + ' LEG BYE';
    if (b.runs === 0) return 'DOT';
    return b.runs + ' RUN' + (b.runs > 1 ? 'S' : '');
  }

  function handleRun(runs) {
    buzz();
    doRecord('runs', runs);
  }

  function handleWide() {
    buzz();
    withBallGuard(() => {
      recordWide($activeMatch, 0);
      triggerAnim({ type: 'WD', text: 'WIDE!', color: '#0891b2', duration: 2000 });
      $lastBallState = { text: '1 WIDE', color: '#0891b2', ts: Date.now() };
      afterBall();
    });
  }

  function handleUndo() {
    const undone = inn && inn.ballLog.length ? inn.ballLog[inn.ballLog.length - 1] : null;
    buzz([10, 40, 30]); // distinct double-pulse so an accidental undo is felt
    cancelPendingInningsEnd();
    undoLastBall($activeMatch);
    persist();
    $lastBallState = { text: '↶ UNDONE: ' + describeBall(undone), color: '#f59e0b', ts: Date.now() };
  }

  function openNextBowler() {
    $modalState = { type: 'selectBowler', payload: { endOver: true } };
  }

  function undoFromInningsBoard() {
    cancelPendingInningsEnd();
    fullScreenBoard.set(null);
    handleUndo();
  }

  function handleSwap() {
    if (!inn) return;
    const tmp = inn.currentStriker;
    // eslint-disable-next-line svelte/no-reactive-reassign
    inn.currentStriker = inn.currentNonStriker;
    // eslint-disable-next-line svelte/no-reactive-reassign
    inn.currentNonStriker = tmp;
    persist();
  }

  function handleChange() {
    $modalState = { type: 'replaceBatsman', payload: { role: 'striker' } };
  }

  function openTriggerMenu() {
    $modalState = { type: 'triggerMenu', payload: {} };
  }

  $: board = match ? $fullScreenBoard : null;
</script>

<div class="scoring-pad" class:free-hit={freeHit}>
  {#if board === 'winner' || board === 'match'}
    <div style="position:absolute; inset:0; background:rgba(15, 23, 42, 0.95); z-index:50; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:24px; text-align:center;">
      <h3 style="color:#10b981; margin:0 0 8px 0; font-size:20px; font-weight:900; letter-spacing:1px;">🏆 MATCH COMPLETE</h3>
      <p style="color:#94a3b8; margin:0 0 24px 0; font-size:13px; font-weight:600;">Saving recording and finalizing broadcast cards...</p>
      <div class="cric-spinner"></div>
    </div>
  {/if}

  {#if board === 'innings1'}
    <div style="position:absolute; inset:0; background:rgba(15, 23, 42, 0.95); z-index:50; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:24px; text-align:center;">
      <h3 style="color:#fbbf24; margin:0 0 8px 0; font-size:18px; font-weight:900; letter-spacing:1px;">FIRST INNINGS COMPLETE</h3>
      <p style="color:#94a3b8; margin:0 0 24px 0; font-size:13px; font-weight:600;">The innings break animation has finished.</p>
      <button class="cric-tile" on:click={() => proceedAfterInningsEnd()} style="font-size:16px; padding:16px 24px; border-radius:12px; background:#2563eb; color:#fff; border:none; font-weight:800; cursor:pointer; width:100%;">
        {match.mode === 'single' ? 'FINISH INNINGS' : 'START 2ND INNINGS ▶'}
      </button>
      {#if undoAvail}
        <button on:click={undoFromInningsBoard} style="margin-top:14px; background:transparent; border:1px solid #4b5563; color:#9ca3af; border-radius:8px; padding:8px 14px; font-size:12px; font-weight:700; cursor:pointer;">
          Wrong entry? ↶ UNDO LAST BALL
        </button>
      {/if}
    </div>
  {/if}

  {#if $currentScreen === 'recording' && $lastBallState}
    <div class="pad-flash" style="background:{$lastBallState.color}">{$lastBallState.text}</div>
  {/if}

  {#if freeHit}
    <div class="fh-banner">● FREE HIT ●</div>
  {/if}

  <div class="pad-grid">
    <!-- Row 1: 0, 1, 2, UNDO -->
    <button class="cric-tile t-dot" disabled={!ready} on:click={() => handleRun(0)} aria-label="Dot ball, no run">
      <span class="tile-num">0</span>
      <span class="tile-sub">DOT</span>
    </button>
    <button class="cric-tile t-run" disabled={!ready} on:click={() => handleRun(1)} aria-label="1 run">
      <span class="tile-num">1</span>
    </button>
    <button class="cric-tile t-run" disabled={!ready} on:click={() => handleRun(2)} aria-label="2 runs">
      <span class="tile-num">2</span>
    </button>
    <!-- UNDO stays enabled during the innings-end pending window — that is
         exactly when a fat-fingered final ball needs reverting. -->
    <button class="cric-tile act-undo" disabled={!undoAvail} on:click={handleUndo} aria-label="Undo last ball">
      <span class="tile-icon">↶</span>
      <span class="tile-sub">UNDO</span>
    </button>

    <!-- Row 2: 3, 4, 6, 5·7 -->
    <button class="cric-tile t-run" disabled={!ready} on:click={() => handleRun(3)} aria-label="3 runs">
      <span class="tile-num">3</span>
    </button>
    <button class="cric-tile t-four" disabled={!ready} on:click={() => handleRun(4)} aria-label="Four, boundary">
      <span class="tile-num">4</span>
      <span class="tile-sub">FOUR</span>
    </button>
    <button class="cric-tile t-six" disabled={!ready} on:click={() => handleRun(6)} aria-label="Six">
      <span class="tile-num">6</span>
      <span class="tile-sub">SIX</span>
    </button>
    <button class="cric-tile act-trigger" disabled={!ready} on:click={openTriggerMenu} aria-label="More options and effects">
      <span class="tile-label">TRIGGERS</span>
      <span class="tile-sub">MORE + FX</span>
    </button>

    <!-- Row 3: WD, NB, BYE, LB -->
    <button class="cric-tile chip-wd" disabled={!ready} on:click={handleWide} aria-label="Wide ball">
      <span class="tile-num">WD</span>
      <span class="tile-sub">WIDE</span>
    </button>
    <button class="cric-tile chip-nb" disabled={!ready} on:click={() => openExtraPicker('nb')} aria-label="No ball">
      <span class="tile-num">NB</span>
      <span class="tile-sub">NO BALL</span>
    </button>
    <button class="cric-tile act-swap" disabled={!inn || !inn.currentStriker || !inn.currentNonStriker} on:click={handleSwap} aria-label="Swap strike between batters">
      <span class="tile-label">SWAP</span>
      <span class="tile-sub">STRIKE</span>
    </button>
    <button class="cric-tile act-change" disabled={!inn || !inn.currentStriker} on:click={handleChange} aria-label="Change batter">
      <span class="tile-label">CHANGE</span>
      <span class="tile-sub">BATTER</span>
    </button>

    <!-- Row 4: full-width wicket — or the next-bowler prompt after an over -->
    {#if awaitingBatter}
      <button class="pad-btn wide" style="background: linear-gradient(135deg, #f59e0b, #d97706); grid-column: 1 / -1; font-size: 15px; font-weight: 800; padding: 16px;" on:click={() => modalState.set({ type: 'selectBatsman' })}>
        🏏 SELECT NEXT BATTER
      </button>
    {/if}
    {#if awaitingBowler}
      <button class="cric-tile t-next-bowler" on:click={openNextBowler} style="grid-column: span 4;" aria-label="Select next bowler, over complete">
        <span class="tile-label wide-label">SELECT NEXT BOWLER →</span>
        <span class="tile-sub">OVER COMPLETE</span>
      </button>
    {:else}
      <button class="cric-tile t-out" disabled={!ready} on:click={() => openWicketPicker()} style="grid-column: span 4;" aria-label="Out, record wicket">
        <span class="tile-label wide-label">OUT</span>
        <span class="tile-sub">WICKET</span>
      </button>
    {/if}
  </div>
</div>

<style>
  .scoring-pad {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }
  .fh-banner {
    text-align: center;
    padding: 4px;
    background: linear-gradient(90deg, #dc2626, #f59e0b);
    color: #fff;
    font-weight: 900;
    font-size: 12px;
    letter-spacing: 2px;
    animation: fh-pulse 0.6s ease-in-out infinite alternate;
  }
  .pad-flash {
    text-align: center;
    padding: 4px;
    font-size: 12px;
    font-weight: 900;
    color: #fff;
    letter-spacing: 1px;
    border-radius: 6px;
    margin: 2px 4px 0;
    animation: fh-pulse 0.3s ease-out;
  }
  .t-next-bowler {
    background: linear-gradient(135deg, #b45309, #f59e0b);
    animation: fh-pulse 0.8s ease-in-out infinite alternate;
  }
  @keyframes fh-pulse {
    from { opacity: 0.7; }
    to { opacity: 1; }
  }
  .pad-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-auto-rows: minmax(0, 1fr);
    gap: 6px;
    flex: 1;
    min-height: 0;
    padding: 6px;
  }

  .cric-tile {
    /* container-type lets tile text scale with each tile's OWN size (cqmin),
       so labels stay inside the tile whether it's a wide phone-portrait pad or
       the narrow landscape record-mode pane. */
    container-type: size;
    position: relative;
    overflow: hidden;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    padding: 2px;
    /* Hairline ring crisply separates adjacent tiles against the dark pane so
       they never look like they bleed into each other. */
    border: 1px solid rgba(255,255,255,0.08);
    cursor: pointer;
    transition: transform 0.12s ease, filter 0.12s ease;
    font-family: inherit;
    -webkit-tap-highlight-color: transparent;
    user-select: none;
    box-shadow: 0 2px 6px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.10);
  }
  .cric-tile:active:not(:disabled) {
    transform: scale(0.93);
    filter: brightness(1.25);
  }
  .cric-tile:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  /* Big number tiles (0-6, WD, NB): scale with tile height, clamped. */
  .tile-num {
    font-size: clamp(16px, 46cqh, 30px);
    font-weight: 900;
    color: #fff;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    text-shadow: 0 1px 4px rgba(0,0,0,0.35);
  }
  /* Word-label tiles (TRIGGERS, SWAP, CHANGE): smaller, never overflow. */
  .tile-label {
    font-size: clamp(10px, 22cqh, 17px);
    font-weight: 900;
    letter-spacing: 0.3px;
    color: #fff;
    line-height: 1.02;
    max-width: 100%;
    padding: 0 2px;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-shadow: 0 1px 3px rgba(0,0,0,0.35);
  }
  /* Full-width action tiles (OUT, SELECT NEXT BOWLER) have room to breathe. */
  .wide-label { font-size: clamp(15px, 30cqh, 24px); letter-spacing: 1px; }
  .tile-sub {
    font-size: clamp(7px, 15cqh, 10px);
    opacity: 0.82;
    color: #fff;
    font-weight: 700;
    letter-spacing: 0.4px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .tile-icon { font-size: clamp(16px, 40cqh, 26px); color: #fff; line-height: 1; }

  /* Tile colors */
  .t-dot { background: #374151; }
  .t-run { background: #1e3a5f; }
  .t-four { background: linear-gradient(135deg, #047857, #10b981); }
  .t-six { background: linear-gradient(135deg, #6d28d9, #8b5cf6); }
  .t-out { background: linear-gradient(135deg, #b91c1c, #ef4444); }
  .chip-wd { background: #155e75; }
  .chip-nb { background: #831843; }
  .act-undo { background: #374151; }
  .act-swap { background: #1e293b; border: 1px solid #334155; }
  .act-change { background: #1e293b; border: 1px solid #334155; }
  .act-trigger { background: linear-gradient(135deg, #92400e, #d97706); }

  .cric-spinner {
    border: 3px solid rgba(255,255,255,0.1);
    border-top: 3px solid #10b981;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    animation: cric-spin 1s linear infinite;
  }
  @keyframes cric-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
</style>
